import { referenceSide, type ReviewState } from '@chess/api-review';
import type { Fen, Phase, Uci } from '@chess/api-model';
import { STAGE_NAME, deltaText, deltaTone, stageHead, type StageHead } from './head.ts';

// What the stage header shows, decided from the review state without React:
// the head of a stage, its title and tooltip, and the score each stage's tab
// carries. `index.tsx` reads the APIs and renders; these decide.

/** Turns an engine move into SAN in a position, when the rules are to hand. */
export type ToSan = (fen: Fen, uci: Uci) => string | undefined;

/** The three stages, in the order their tabs stand. */
export const PHASES: readonly Phase[] = ['opening', 'middlegame', 'ending'];

/** What a stage head is built from, all of it from the review session. */
export function headOf(s: Pick<ReviewState, 'game' | 'scan' | 'reader'>, phase: Phase, toSan: ToSan): StageHead | undefined {
  return s.game ? stageHead(s.scan?.scan, s.game, phase, referenceSide(s), toSan, s.scan?.rules) : undefined;
}

/** The book's reading of the opening, as far as the title needs it. */
export interface OpeningReading {
  opening: { name: string; eco: string };
}

/** The opening's family name ("Sicilian Defense") for the opening once the book has read it, the stage's name otherwise. */
export function stageTitle(phase: Phase, reading: OpeningReading | undefined): string {
  return phase === 'opening' && reading ? reading.opening.name.split(': ')[0] : STAGE_NAME[phase];
}

/** The opening's full name and ECO code, on the opening's title only. */
export function stageTitleTooltip(phase: Phase, reading: OpeningReading | undefined): string | undefined {
  return phase === 'opening' && reading ? `${reading.opening.name} · ECO ${reading.opening.eco}` : undefined;
}

/** A stage's score on its tab: the change across it, and its tone; neither when the game ended before it or a score is missing. */
export interface StageBadge {
  phase: Phase;
  text?: string;
  tone?: 'good' | 'bad' | 'level';
}

/** The badge of one stage from its head. */
export function badgeOf(phase: Phase, head: StageHead | undefined): StageBadge {
  const text = head && !head.ended ? deltaText(head) : undefined;
  return { phase, text, tone: text && head ? deltaTone(head) : undefined };
}

/** Every stage's badge, in tab order, for a review state. */
export function stageBadges(s: Pick<ReviewState, 'game' | 'scan' | 'reader'>, toSan: ToSan): StageBadge[] {
  return PHASES.map((phase) => badgeOf(phase, headOf(s, phase, toSan)));
}
